import { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { Breadcrumb, message, Table } from "antd";

import routeNames from "constants/route-names";
import { useGetUserByIdQuery } from "graphql/queries/get-user-by-id.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { canRoleEdit, canRoleRemove } from "utils/permissions-management";
import { getPropertyAddress } from "utils/helpers";
import { UserRoleValue } from "constants/field-values";
import { useUpdateUserByIdMutation } from "graphql/mutations/update-user-by-id.generated";
import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";

const PropertiesColumns = [
	{
		title: "ID",
		dataIndex: "id",
		key: `ID`
	},
	{
		title: "ADDRESS",
		key: `ADDRESS`,
		render: (record: any) => getPropertyAddress(record.address, record.standardized_address)
	},
	{
		title: "NAME",
		dataIndex: "name",
		key: `NAME`
	},
	{
		title: "STATUS",
		dataIndex: "tier_status",
		key: `STATUS`
	}
];

const UserDetail: React.FC = () => {
	const params = useParams<{ userId: string }>();
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const [userData, setUserData] = useState<any>(null);
	const [propertiesData, setPropertiesData] = useState<Array<any>>([]);
	const [userOrganizationName, setUserOrganizationName] = useState<string>("");
	const canUserEdit = useMemo(() => {
		return (
			loggedUserValue?.organization_id === userData?.organization_id &&
			canRoleEdit(loggedUserValue.role as UserRoleValue, userData?.role as UserRoleValue)
		);
	}, [loggedUserValue, userData]);

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("UserDetail Error Get Users: ", error);
	}, []);

	const handleOnCompletedGetUsers = useCallback(data => {
		const users_by_pk = data.users_by_pk[0];

		if (!users_by_pk) {
			message.error("User not found.");
			navigate(routeNames.ADMIN_USERS);
			return;
		}

		setUserData(users_by_pk);

		const { user_properties, organization } = users_by_pk;
		const properties = user_properties.filter(prop => prop.property).map(element => element.property);
		setPropertiesData(properties);

		if (organization) {
			setUserOrganizationName(organization.name);
			return;
		}

		if (user_properties && user_properties.length) {
			const propWithOrg = user_properties.find(
				userprop => userprop && userprop.property && userprop.property.organization
			);

			if (propWithOrg) {
				const { property } = propWithOrg;
				setUserOrganizationName(property.organization.name);
				return;
			}
		}
	}, []);

	const { loading } = useGetUserByIdQuery({
		skip: !params.userId,
		variables: {
			id: params.userId || "",
			is_not_deleted: true
		},
		errorPolicy: "all",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers,
		onCompleted: handleOnCompletedGetUsers
	});

	const handleOnClickEdit = useCallback(() => {
		if (params.userId) {
			const path = `${routeNames.ADMIN_USER_EDIT.replace(":userId", params.userId)}`;
			navigate(path);
		}
	}, [params.userId]);

	const [deleteUser] = useUpdateUserByIdMutation({
		variables: {
			id: params.userId || "",
			set: {
				deleted_at: new Date(),
				deleted_by: loggedUserValue.id
			}
		},
		onCompleted: () => {
			message.success("User deleted successfully");
			navigate(routeNames.ADMIN_USERS);
		},
		onError: error => {
			console.error("Error updating user:", error);
		}
	});

	const handleDeleteUser = useCallback(() => {
		const isCurrentUser = loggedUserValue.id === params.userId;
		const canBeRemoved = canRoleRemove(loggedUserValue.role as UserRoleValue, userData?.role as UserRoleValue);

		if (isCurrentUser) {
			message.error("You cannot delete your own user account.");
			return;
		}

		if (!canBeRemoved) {
			message.error("You do not have permission to remove this user.");
			return;
		}

		if (propertiesData.length) {
			message.error("Cannot delete user with assigned properties. Please remove the properties first.");
			return;
		}

		deleteUser();
	}, [params.userId, userData, loggedUserValue, propertiesData]);

	const isLoading = loading;

	return (
		<div className="user-d-screen-container">
			<Breadcrumb
				className="products-breadcrumbs"
				items={[
					{
						title: "Users",
						onClick: () => {
							navigate(routeNames.ADMIN_USERS);
						}
					},
					{
						title: userData ? `${userData?.first_name} ${userData?.last_name}` : ""
					}
				]}
			/>
			<div className="user-d-container">
				{userData?.profile_photo ? (
					<img src={userData?.profile_photo} className="user-d-photo" />
				) : (
					<img src={process.env.PUBLIC_URL + "/assets/images/icons/sidebar-users.svg"} className="user-d-photo" />
				)}
				<div className="organization-d-info-container">
					<div className="organization-d-row">
						<span className="organization-d-name">{`${userData?.first_name} ${userData?.last_name}`}</span>
						<div>
							{canUserEdit && (
								<>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className="selected-file-icon"
										onClick={handleOnClickEdit}
									/>
									<RemovePopconfirm
										title="Delete User"
										description="Are you sure to delete this user?"
										handleDeleteClick={handleDeleteUser}
									/>
								</>
							)}
						</div>
					</div>

					<div className="organization-d-row">
						{userOrganizationName ? (
							<span className="user-organization-d-name">{`${userOrganizationName}`}</span>
						) : null}
						{userData?.title ? <span className="user-organization-d-name">{`${userData?.title}`}</span> : null}
					</div>

					<div className="organization-d-row">
						{userData?.phone_number ? (
							<span className="user-organization-d-info">{`${userData?.phone_number}`}</span>
						) : null}
						{userData?.email ? <span className="user-organization-d-info">{`${userData?.email}`}</span> : null}
					</div>
				</div>
			</div>
			<div className="organization-d-table-container">
				<div className="organization-d-row">
					<span className="organization-d-name">{"Properties"}</span>
				</div>
				<Table
					dataSource={propertiesData}
					columns={PropertiesColumns}
					loading={isLoading}
					rowKey={record => record.id}
					onRow={(record, rowIndex) => {
						return {
							onClick: event => {
								// handleOnClickOrganization(record);
							}
						};
					}}
				/>
			</div>
		</div>
	);
};

export default UserDetail;
